/**
 * The building access agreement a landlord signs before we cable their block.
 *
 * ## What this is for
 *
 * Fibre into a block of flats means drilling somebody else's walls, running
 * trunking through their corridors and leaving a distribution box in their
 * riser. None of that is the tenant's to authorise, and doing it on a verbal
 * yes from a caretaker is how a technician ends up in an argument on a roof
 * about equipment nobody can prove was agreed to. This is the paper that
 * settles ownership, access and who pays when a cable is cut.
 *
 * ## Why it is a third contract type rather than a variant of the other two
 *
 * The customer agreement is about a service and a monthly fee; the employment
 * letter is about a person and a salary. This is about a *place* — and the
 * party signing it is usually not a customer and never an employee. Bending
 * either of the existing models around it would have meant a `Contract` row
 * with a null package and a meaningless term in months, which is the shape of
 * thing that later gets counted in a revenue report by mistake.
 *
 * What it *does* share is the signing machinery in `./signing.js`, which was
 * built party-agnostic for exactly this: `company` signs, then `counterparty`,
 * with the order derived from the signatures rather than stored.
 */
/* ── How long it runs ─────────────────────────────────────────────────────── */
export const LANDLORD_TERMS = ['one_year', 'three_years', 'five_years', 'indefinite'];
export const LANDLORD_TERM_LABEL = {
    one_year: '1 year',
    three_years: '3 years',
    five_years: '5 years',
    indefinite: 'Indefinitely',
};
/** Months, or null for an indefinite term. Used to compute an end date. */
export const LANDLORD_TERM_MONTHS = {
    one_year: 12,
    three_years: 36,
    five_years: 60,
    indefinite: null,
};
export function isLandlordTerm(value) {
    return typeof value === 'string' && LANDLORD_TERMS.includes(value);
}
/* ── What the installation costs ──────────────────────────────────────────── */
export const FEE_BASES = ['free', 'one_time', 'other'];
export const FEE_BASIS_LABEL = {
    free: 'Free of charge',
    one_time: 'A one-time fee',
    other: 'Another arrangement',
};
export function isFeeBasis(value) {
    return typeof value === 'string' && FEE_BASES.includes(value);
}
/* ── Exclusivity ──────────────────────────────────────────────────────────── */
/**
 * Whether this building is ours alone, and for how long.
 *
 * Clause 4 of the paper agreement says that unless expressly agreed otherwise
 * **in writing**, the landlord stays free to let other providers install their
 * own infrastructure. This is that writing. It was a free-text box labelled
 * "Exclusivity agreed" whose hint explained that leaving it blank meant none —
 * which put the whole grant behind knowing that the thing to type was a
 * duration. Nobody types a duration into a box labelled "agreed".
 *
 * So it is a choice with three answers, and the first is what a blank field
 * has always meant. `none` stays the default because an empty box must never
 * become an accidental exclusive grant: this is the clause a landlord is most
 * likely to be held to years later, and the expensive mistake is granting
 * exclusivity nobody meant to give.
 */
export const EXCLUSIVITY_BASES = ['none', 'whole_term', 'period'];
export const EXCLUSIVITY_BASIS_LABEL = {
    none: 'Not granted — other providers may also install',
    whole_term: 'Granted for the whole term of this agreement',
    period: 'Granted for a stated period',
};
export function isExclusivityBasis(value) {
    return typeof value === 'string' && EXCLUSIVITY_BASES.includes(value);
}
/* ── The fields, split by who fills them ──────────────────────────────────── */
/**
 * ## Why the technical schedule is ours and not the landlord's
 *
 * Schedule A asks where the fibre enters, where the distribution box goes and
 * which cable routes are approved. A landlord does not know any of that — it
 * is the output of our own site survey, and asking them for it would mean an
 * agreement that cannot be signed until somebody guesses. So Schedule A sits
 * on the company's half, filled in from the survey before the agreement goes
 * out, and the landlord is agreeing *to* it rather than supplying it.
 *
 * What the landlord genuinely knows, and therefore fills: who their caretaker
 * is, who manages access, and their own identity document number.
 *
 * ## Why the landlord's contact details are on the company's half
 *
 * Because the agreement is emailed to them. The address has to exist before
 * there is anything to send, so it is captured when the agreement is raised —
 * and `landlordEmail` is required for that reason and no other. The landlord
 * then confirms it on their own copy.
 *
 * ## Why the witness is optional
 *
 * The paper version has a witness block and it is worth having. Requiring it
 * would mean an agreement that cannot be completed because nobody happened to
 * be standing there — so it is present, prompted, and not a blocker.
 */
export function landlordFields() {
    return [
        /* ── The property ──────────────────────────────────────────────────── */
        {
            key: 'propertyName',
            label: 'Property name',
            kind: 'text',
            party: 'company',
            required: true,
            hint: 'As people refer to it locally — “Riverside Court”.',
        },
        {
            key: 'physicalAddress',
            label: 'Physical address',
            kind: 'text',
            party: 'company',
            required: true,
        },
        {
            key: 'plotNumber',
            label: 'Plot / L.R. number',
            kind: 'text',
            party: 'company',
            required: false,
            hint: 'If the title is to hand. Not every block has one written down.',
        },
        /* ── Who we are dealing with, and where to send it ─────────────────── */
        /*
         * The meter the equipment draws from, on the agreement rather than only in
         * the power module. The number is what every token purchase is made
         * against, so an agreement that does not name it cannot show the Landlord
         * what was bought for them.
         */
        {
            key: 'powerMeterNumber',
            label: 'Meter the equipment draws from',
            kind: 'text',
            party: 'company',
            required: false,
            hint: 'The eleven-digit KPLC prepaid meter number. Leave blank if the property is not on a prepaid meter.',
        },
        {
            key: 'powerMeterHolder',
            label: 'Meter account holder',
            kind: 'text',
            party: 'company',
            required: false,
            hint: 'Whose name the meter is in, where that is not the Landlord.',
        },
        {
            key: 'landlordName',
            label: 'Landlord’s full name',
            kind: 'text',
            party: 'company',
            required: true,
        },
        {
            key: 'landlordEmail',
            label: 'Landlord’s email',
            kind: 'text',
            party: 'company',
            required: true,
            hint: 'Required — the agreement is sent here for them to sign.',
        },
        {
            key: 'landlordPhone',
            label: 'Landlord’s telephone',
            kind: 'text',
            party: 'company',
            required: true,
        },
        /* ── The commercial terms ──────────────────────────────────────────── */
        {
            key: 'term',
            label: 'How long it runs',
            kind: 'text',
            party: 'company',
            required: true,
            hint: 'One of: one_year, three_years, five_years, indefinite.',
        },
        {
            key: 'terminationNoticeDays',
            label: 'Notice to terminate (days)',
            kind: 'text',
            party: 'company',
            required: true,
            hint: 'Both sides give the same notice. 30 is usual.',
        },
        {
            key: 'feeBasis',
            label: 'Installation fee',
            kind: 'choice',
            party: 'company',
            required: true,
            options: FEE_BASES.map((basis) => ({ value: basis, label: FEE_BASIS_LABEL[basis] })),
        },
        {
            key: 'feeAmountKes',
            label: 'Fee (KES)',
            kind: 'money',
            party: 'company',
            required: false,
            hint: 'Only when a one-time fee was agreed.',
        },
        {
            key: 'feeOther',
            label: 'Other fee arrangement',
            kind: 'text',
            party: 'company',
            required: false,
        },
        {
            key: 'exclusivityBasis',
            label: 'Exclusivity for this property',
            kind: 'choice',
            party: 'company',
            required: false,
            options: EXCLUSIVITY_BASES.map((basis) => ({
                value: basis,
                label: EXCLUSIVITY_BASIS_LABEL[basis],
            })),
            hint: 'Clause 4. Left alone, no exclusivity is granted and other providers may install.',
        },
        {
            key: 'exclusivity',
            label: 'Exclusive for how long',
            kind: 'text',
            party: 'company',
            required: false,
            hint: 'Only when exclusivity is granted for a stated period — for example, the first two years.',
        },
        {
            key: 'specialTerms',
            label: 'Special terms',
            kind: 'text',
            party: 'company',
            required: false,
        },
        /* ── Schedule A: what the survey found ─────────────────────────────── */
        {
            key: 'numberOfBuildings',
            label: 'Number of buildings',
            kind: 'text',
            party: 'company',
            required: false,
        },
        {
            key: 'approximateUnits',
            label: 'Approximate number of units',
            kind: 'text',
            party: 'company',
            required: false,
        },
        {
            key: 'mainFibreEntryPoint',
            label: 'Main fibre entry point',
            kind: 'text',
            party: 'company',
            required: false,
        },
        {
            key: 'distributionBoxLocation',
            label: 'Main distribution box location',
            kind: 'text',
            party: 'company',
            required: false,
        },
        {
            key: 'approvedCableRoutes',
            label: 'Approved cable routes',
            kind: 'text',
            party: 'company',
            required: false,
        },
        /* ── Signing for the business ──────────────────────────────────────── */
        {
            key: 'companySignatoryName',
            label: 'Signing for GoSpeedNet — name',
            kind: 'text',
            party: 'company',
            required: true,
        },
        {
            key: 'companySignatoryTitle',
            label: 'Signing for GoSpeedNet — designation',
            kind: 'text',
            party: 'company',
            required: true,
            hint: 'For example Chief Executive Officer.',
        },
        {
            key: 'companySignature',
            label: 'GoSpeedNet signature',
            kind: 'signature',
            party: 'company',
            required: true,
        },
        /* ── The landlord's half ───────────────────────────────────────────── */
        {
            key: 'landlordIdNumber',
            label: 'Your national ID or company registration number',
            kind: 'text',
            party: 'counterparty',
            required: true,
        },
        {
            key: 'propertyManagerName',
            label: 'Property manager or caretaker',
            kind: 'text',
            party: 'counterparty',
            required: false,
            hint: 'Whoever lets a technician in. Leave blank if that is you.',
        },
        {
            key: 'propertyManagerPhone',
            label: 'Their telephone',
            kind: 'text',
            party: 'counterparty',
            required: false,
        },
        {
            key: 'landlordSignature',
            label: 'Your signature',
            kind: 'signature',
            party: 'counterparty',
            required: true,
        },
        {
            key: 'witnessName',
            label: 'Witness — full name',
            kind: 'text',
            party: 'counterparty',
            required: false,
            hint: 'Optional. Worth having, and not worth delaying the agreement for.',
        },
        {
            key: 'witnessIdNumber',
            label: 'Witness — national ID',
            kind: 'text',
            party: 'counterparty',
            required: false,
        },
        {
            key: 'witnessSignature',
            label: 'Witness signature',
            kind: 'signature',
            party: 'counterparty',
            required: false,
        },
    ];
}
/* ── The agreement itself ─────────────────────────────────────────────────── */
/**
 * The wording an agreement is raised with, gaps and all.
 *
 * Copied onto each agreement when it is raised, for the same reason the
 * employment letter is: improving this template must never change what
 * somebody has already signed. A test asserts that every `{{placeholder}}`
 * here is a field the form collects and that every required field appears in
 * the text — either kind of drift is silent, and one of them prints a blank
 * rule on every agreement for ever.
 *
 * The checkbox rows in the paper version are rendered as the answer that was
 * chosen rather than as empty boxes. A printed “☐ 1 Year ☐ 3 Years” with
 * nothing ticked is worse than useless on a signed document.
 */
export function landlordAgreementBody() {
    return [
        '# LANDLORD BUILDING INTERNET CABLE INSTALLATION & ACCESS AGREEMENT',
        '',
        '| | |',
        '|---|---|',
        '| Agreement reference | {{agreementReference}} |',
        '| Date | {{agreementDate}} |',
        '',
        '## BETWEEN',
        '',
        '**GOSPEEDNET**, hereinafter **“the Service Provider”**',
        '',
        'and',
        '',
        '| | |',
        '|---|---|',
        '| Landlord / property owner | {{landlordName}} |',
        '| National ID / company registration no. | {{landlordIdNumber}} |',
        '| Telephone | {{landlordPhone}} |',
        '| Email | {{landlordEmail}} |',
        '| Property name | {{propertyName}} |',
        '| Physical address | {{physicalAddress}} |',
        '| Plot / L.R. no. | {{plotNumber}} |',
        '',
        'hereinafter **“the Landlord”**. Together, **“the Parties”**.',
        '',
        '## 1. Purpose',
        '',
        'This Agreement grants GoSpeedNet permission to install, operate, maintain, repair, replace and upgrade internet communication infrastructure within the Landlord’s property.',
        '',
        'That infrastructure may include fibre-optic and Ethernet cabling, conduits and trunking, fibre distribution boxes, network switches, optical network equipment, routers and access points, cabinets, and the poles and brackets that support them.',
        '',
        'It is used to provide internet and related communication services to authorised tenants, occupants and customers within the property.',
        '',
        '## 2. Grant of installation rights',
        '',
        'The Landlord grants GoSpeedNet and its authorised employees, contractors and technicians reasonable permission to enter and access the property in order to:',
        '',
        '- conduct a site survey;',
        '- install fibre-optic and network cabling;',
        '- install distribution boxes and associated equipment;',
        '- connect individual tenants and customers;',
        '- test and commission the network;',
        '- inspect, maintain and repair GoSpeedNet infrastructure;',
        '- replace damaged or obsolete equipment;',
        '- upgrade the network where reasonably necessary;',
        '- remove GoSpeedNet equipment on termination of this Agreement.',
        '',
        'The Landlord shall provide the reasonable cooperation needed to facilitate installation.',
        '',
        /*
         * Said explicitly, because a standing right to enter land can be argued to
         * be an easement — and an easement is an interest in land that has to be
         * registered under the Land Registration Act, 2012. An unregistered one is
         * a dispute waiting for a change of ownership, which clause 19 shows both
         * parties already expect to happen.
         */
        'This permission is a contractual licence for the purposes set out above. It does not create a lease, an easement, a wayleave or any other interest in the property, and neither Party intends it to be registrable under the Land Registration Act, 2012. GoSpeedNet occupies no part of the property as a tenant.',
        '',
        '## 3. Approved installation areas',
        '',
        'Infrastructure may be installed in appropriate common or service areas — external walls, internal common areas, corridors, staircases, service ducts, ceilings, utility areas, risers, gatehouses and equipment rooms — and anywhere else the Parties agree.',
        '',
        'GoSpeedNet shall make reasonable efforts to route cabling neatly, securely and professionally.',
        '',
        'The routes approved for this property are: **{{approvedCableRoutes}}**',
        '',
        '## 4. Installation and workmanship',
        '',
        'GoSpeedNet shall use reasonable care and professional workmanship. Where drilling, fastening, clipping or trunking is reasonably necessary, the Landlord authorises that work.',
        '',
        'Where reasonably practicable GoSpeedNet shall minimise disruption to occupants, keep installation areas reasonably clean, avoid unnecessary damage, secure cables and equipment properly, and follow the applicable technical and safety requirements.',
        '',
        '## 5. Ownership of the infrastructure',
        '',
        'Unless otherwise agreed in writing, all GoSpeedNet-owned infrastructure installed at the property remains the property of GoSpeedNet — cabling, distribution boxes, switches, routers, fibre equipment, cabinets, access points, power and communication equipment and their accessories.',
        '',
        'The Landlord shall not sell, transfer, remove, relocate, modify, disconnect or interfere with it without GoSpeedNet’s prior written authorisation.',
        '',
        '## 6. Tenant connections',
        '',
        'Tenants and occupants may subscribe to GoSpeedNet services independently. The Landlord shall permit reasonable access for technicians to connect authorised customers within the building.',
        '',
        'Individual subscriptions, payments, upgrades, disconnections and support are governed by the customer agreement between GoSpeedNet and that customer. The Landlord is not responsible for a tenant’s subscription payments unless separately agreed in writing.',
        '',
        '## 7. Access for maintenance',
        '',
        'The Landlord shall provide reasonable access to GoSpeedNet equipment and cable routes for maintenance, troubleshooting, repair and upgrade.',
        '',
        'Where practicable GoSpeedNet shall notify the Landlord, property manager or caretaker before planned maintenance. In a fault or an emergency GoSpeedNet may undertake the urgent work needed to restore service, and shall notify the Landlord as soon as reasonably practicable.',
        '',
        '## 8. Property manager or caretaker',
        '',
        '| | |',
        '|---|---|',
        '| Manager / caretaker | {{propertyManagerName}} |',
        '| Telephone | {{propertyManagerPhone}} |',
        '',
        'The Landlord shall ensure that person is aware of this Agreement and of GoSpeedNet’s authorised access rights.',
        '',
        '## 9. Installation fees',
        '',
        'The initial building infrastructure installation is: **{{feeDescription}}**',
        '',
        'Any charge for an individual customer’s installation is communicated to that customer before the work is undertaken.',
        '',
        '## 10. Maintenance and repair',
        '',
        'GoSpeedNet shall maintain its infrastructure. Where damage arises from normal wear, environmental factors or network faults, GoSpeedNet shall undertake reasonable repair or replacement.',
        '',
        'Where damage arises from intentional acts, unauthorised interference, negligence or unauthorised relocation, GoSpeedNet may recover the reasonable cost of repair or replacement from the responsible party.',
        '',
        '## 11. Protection of cables and equipment',
        '',
        'The Landlord shall take reasonable measures to protect GoSpeedNet infrastructure in common areas under their control, and shall notify GoSpeedNet where construction, renovation or demolition may affect it.',
        '',
        'No person shall intentionally cut, disconnect, relocate, conceal, damage or interfere with the infrastructure without authorisation.',
        '',
        '## 12. Other service providers',
        '',
        'Unless expressly agreed otherwise in writing, this Agreement does not prevent the Landlord from permitting other internet service providers to install their own infrastructure. Each provider is responsible for its own equipment.',
        '',
        'Exclusivity for this property: **{{exclusivityDescription}}**',
        '',
        'Special terms: {{specialTerms}}',
        '',
        '## 13. The Landlord’s responsibilities',
        '',
        '- Grant reasonable access to authorised GoSpeedNet personnel.',
        '- Provide accurate information about the property.',
        '- Inform GoSpeedNet of planned construction or renovation affecting the network.',
        '- Prevent unauthorised removal of, or interference with, GoSpeedNet equipment so far as reasonably possible.',
        '- Notify GoSpeedNet of any damage discovered.',
        '- Cooperate in facilitating tenant connections.',
        '',
        '## 14. GoSpeedNet’s responsibilities',
        '',
        '- Use qualified or authorised personnel for installation and maintenance.',
        '- Install neatly and professionally.',
        '- Take reasonable precautions to protect the property.',
        '- Maintain its infrastructure.',
        '- Respond reasonably to reported faults.',
        '- Remove its equipment where required following lawful termination.',
        '- Comply with applicable laws, regulations and technical requirements.',
        '- Pay for the electricity its equipment draws, as set out below.',
        '',
        /*
         * The arrangement the whole power-token module exists to run, and it was
         * written down nowhere.
         *
         * GoSpeedNet's equipment sits in the Landlord's building and draws power
         * from the Landlord's prepaid meter. Unwritten, that is a bill somebody
         * eventually notices and disputes — a year of power taken with nothing
         * agreed about who owed it, and no way to show what was already paid.
         *
         * It is settled by buying tokens for the Landlord's own meter rather than
         * by paying money, which matters twice: the Landlord keeps whatever is
         * bought and unused, and GoSpeedNet is not reselling electricity, which
         * would be a licensable activity under the Energy Act, 2019.
         */
        '',
        'GoSpeedNet equipment installed at the property draws electricity from the meter stated below. GoSpeedNet shall meet that cost by buying prepaid electricity tokens for that meter, at the times and amounts agreed between the Parties, and shall record every purchase in its system. On request the Landlord shall be given a statement of the tokens bought for the meter.',
        '',
        '| | |',
        '|---|---|',
        '| Meter the equipment draws from | {{powerMeterNumber}} |',
        '| Meter account holder | {{powerMeterHolder}} |',
        '',
        'Tokens bought under this clause are credited to the Landlord’s meter and belong to the Landlord; any balance remaining is the Landlord’s and is not refundable to GoSpeedNet. The Landlord shall tell GoSpeedNet before changing or replacing the meter, so that purchases are not made against a meter that has been disconnected.',
        '',
        'If the Landlord asks to be paid in money rather than in tokens, or if the property is not on a prepaid meter, the Parties shall agree the amount and the method in writing before the arrangement changes.',
        '',
        '## 15. Property damage',
        '',
        'GoSpeedNet shall exercise reasonable care. Where it causes material physical damage through negligence or improper workmanship, the Parties shall agree reasonable rectification.',
        '',
        'GoSpeedNet is not responsible for damage arising from pre-existing defects, third-party interference, unauthorised alterations, natural events, or anything outside its reasonable control.',
        '',
        '## 16. Safety and compliance',
        '',
        'Both Parties shall cooperate so that installation and maintenance are carried out safely. GoSpeedNet shall take reasonable precautions around electrical installations, building structures and other utilities. The Landlord shall disclose known hazards or restrictions affecting the work.',
        '',
        '## 17. Duration',
        '',
        'This Agreement commences on the date of signing and runs for: **{{termDescription}}**',
        '',
        'unless terminated in accordance with clause 18.',
        '',
        '## 18. Termination',
        '',
        'Either Party may terminate by giving **{{terminationNoticeDays}} days’** written notice.',
        '',
        'On termination GoSpeedNet shall have reasonable access to disconnect and remove its infrastructure where appropriate. The Parties may instead agree in writing that some or all of it remains in place. Termination does not affect financial obligations incurred before it.',
        '',
        '## 19. Change of ownership',
        '',
        'If the property is sold, transferred or placed under new management, the Landlord shall where reasonably practicable notify the new owner or manager of this Agreement. The Parties may agree to transfer or continue it.',
        '',
        '## 20. Confidentiality and customer privacy',
        '',
        'The Parties shall respect the confidentiality and privacy of customer information. The Landlord shall not access or disclose customer passwords, account information, private communications or other confidential customer information except where legally authorised.',
        '',
        /*
         * Named, because a landlord handed a list of which flats have service is
         * holding personal data and has duties of their own under the Act — not
         * merely a courtesy owed to us.
         */
        'Information about tenants and customers is personal data. Each Party shall handle it in accordance with Article 31 of the Constitution of Kenya and the Data Protection Act, 2019, shall use it only for the purposes of this Agreement, and shall not disclose it to any other person without lawful authority.',
        '',
        '## 21. Limitation of liability',
        '',
        'Each Party is responsible for loss or damage arising directly from its own negligence, unlawful conduct or intentional acts. Neither is liable for events outside its reasonable control — natural disasters, vandalism, civil disturbance, utility failure or the actions of unrelated third parties — subject to applicable law.',
        '',
        '## 22. Dispute resolution and governing law',
        '',
        'The Parties shall first attempt to resolve any disagreement through good-faith consultation. Failing that, they may pursue mediation, arbitration or the legal remedies available under the laws of Kenya.',
        '',
        /*
         * There was no governing-law clause at all — only a passing mention of
         * Kenyan remedies inside the disputes paragraph, which is not the same
         * thing and is the first point taken in an argument about jurisdiction.
         */
        'This Agreement is governed by, and construed in accordance with, the laws of Kenya, and the Parties submit to the jurisdiction of the Kenyan courts.',
        '',
        '## 23. Notices',
        '',
        'Formal notice may be given by written notice, email or other verifiable communication.',
        '',
        '| | |',
        '|---|---|',
        '| Company | GoSpeedNet |',
        '| Telephone | {{companyPhone}} |',
        '| Email | {{companyEmail}} |',
        '| Address | {{companyAddress}} |',
        '',
        '## 24. Entire agreement',
        '',
        'This Agreement represents the understanding between the Parties concerning the installation and operation of GoSpeedNet infrastructure at the property. Any amendment or variation shall be made in writing and acknowledged by both Parties.',
        '',
        '## 25. Declaration',
        '',
        'By signing below, both Parties confirm that they have read, understood and voluntarily agreed to the terms and conditions contained in this Agreement.',
        '',
        '---',
        '',
        '**FOR GOSPEEDNET**',
        '',
        '| | |',
        '|---|---|',
        '| Name | {{companySignatoryName}} |',
        '| Designation | {{companySignatoryTitle}} |',
        '| Signed | {{companySignature}} |',
        '',
        '**THE LANDLORD / PROPERTY OWNER**',
        '',
        '| | |',
        '|---|---|',
        '| Name | {{landlordName}} |',
        '| National ID / registration no. | {{landlordIdNumber}} |',
        '| Telephone | {{landlordPhone}} |',
        '| Email | {{landlordEmail}} |',
        '| Signed | {{landlordSignature}} |',
        '',
        '**WITNESS**',
        '',
        '| | |',
        '|---|---|',
        '| Name | {{witnessName}} |',
        '| National ID | {{witnessIdNumber}} |',
        '| Signed | {{witnessSignature}} |',
        '',
        '---',
        '',
        '# SCHEDULE A — BUILDING AND INSTALLATION DETAILS',
        '',
        '| | |',
        '|---|---|',
        '| Property | {{propertyName}} |',
        '| Location | {{physicalAddress}} |',
        '| Plot / L.R. no. | {{plotNumber}} |',
        '| Number of buildings | {{numberOfBuildings}} |',
        '| Approximate units | {{approximateUnits}} |',
        '| Main fibre entry point | {{mainFibreEntryPoint}} |',
        '| Main distribution box | {{distributionBoxLocation}} |',
        '| Approved cable routes | {{approvedCableRoutes}} |',
        '',
        '# SCHEDULE B — CONTACTS',
        '',
        '| Role | Name | Telephone |',
        '|---|---|---|',
        '| Landlord | {{landlordName}} | {{landlordPhone}} |',
        '| Property manager / caretaker | {{propertyManagerName}} | {{propertyManagerPhone}} |',
        '| GoSpeedNet | {{companySignatoryName}} | {{companyPhone}} |',
        '',
    ].join('\n');
}
/* ── The sentences that are assembled rather than typed ───────────────────── */
/**
 * The three values the document quotes that nobody types directly.
 *
 * `feeDescription`, `exclusivityDescription` and `termDescription` are each a
 * whole clause of the agreement, and each is built from a choice plus an
 * optional amount. Composing them here — rather than in the service, or worse
 * in the page — keeps the wording of a signed clause in one testable place,
 * and means the same words appear on screen, in the email and on the print.
 */
export function landlordDerivedValues(values) {
    const text = (key) => {
        const value = values[key];
        return value === undefined || value === null ? '' : String(value).trim();
    };
    const basis = text('feeBasis');
    const amount = text('feeAmountKes');
    const feeDescription = basis === 'free'
        ? 'free of charge'
        : basis === 'one_time'
            ? `a one-time fee of KES ${amount || '__________'}`
            : basis === 'other'
                ? text('feeOther') || 'as separately agreed'
                : '';
    /*
     * Exclusivity, written out in full.
     *
     * `exclusivityBasis` is the answer; `exclusivity` is the period, and only
     * means anything alongside `period`.
     *
     * The fallback matters. Agreements raised before this was a choice have no
     * basis recorded and a period in the free-text box, and one of them is
     * already signed. Reading the old shape when the new one is absent keeps
     * those rendering exactly the words they were signed with — the clause is
     * hashed, and a document whose text changes under it is a document whose
     * signatures stop proving anything.
     */
    const period = text('exclusivity');
    const basisGiven = text('exclusivityBasis');
    const exclusivityBasis = isExclusivityBasis(basisGiven)
        ? basisGiven
        : period
            ? 'period'
            : 'none';
    const exclusivityDescription = exclusivityBasis === 'whole_term'
        ? `granted to GoSpeedNet for the whole term of this Agreement`
        : exclusivityBasis === 'period'
            ? `granted to GoSpeedNet for ${period || '__________'}`
            : 'not granted — the Landlord may permit other providers to install their own infrastructure';
    const term = text('term');
    const termDescription = isLandlordTerm(term)
        ? term === 'indefinite'
            ? 'an indefinite period'
            : LANDLORD_TERM_LABEL[term]
        : '';
    return { feeDescription, exclusivityDescription, termDescription };
}
/**
 * Every `{{key}}` the body asks for that no field supplies.
 *
 * Three are composed by `landlordDerivedValues`; the rest come from the
 * agreement row or from settings — the reference, the date it was raised, and
 * the company's own contact block, none of which a person should be retyping
 * onto every agreement.
 */
export const LANDLORD_SUPPLIED_KEYS = [
    'agreementReference',
    'agreementDate',
    'feeDescription',
    'exclusivityDescription',
    'termDescription',
    'companyPhone',
    'companyEmail',
    'companyAddress',
];
//# sourceMappingURL=landlord-agreement.js.map